/**
 * Client-side helpers to normalize uploads before /api/storage/upload.
 * AVIF is accepted by modern browsers but often fails server-side (Sharp
 * builds without libavif) or as a guest fallback — convert to JPEG first.
 */

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

function isAvifFile(file: File): boolean {
  const type = (file.type || "").toLowerCase();
  return type === "image/avif" || file.name.toLowerCase().endsWith(".avif");
}

/**
 * Returns a data URL suitable for upload. AVIF is re-encoded to JPEG via canvas.
 */
export async function fileToUploadDataUrl(file: File): Promise<string> {
  if (!isAvifFile(file)) {
    return readFileAsDataUrl(file);
  }

  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close();
      throw new Error("Could not convert AVIF (canvas unavailable)");
    }
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();
    return canvas.toDataURL("image/jpeg", 0.92);
  } catch (err) {
    console.warn("[fileToUploadDataUrl] AVIF convert failed, trying Image element:", err);
    // Fallback: HTMLImageElement decode
    const objectUrl = URL.createObjectURL(file);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error("Browser could not decode AVIF"));
        el.src = objectUrl;
      });
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Could not convert AVIF (canvas unavailable)");
      ctx.drawImage(img, 0, 0);
      return canvas.toDataURL("image/jpeg", 0.92);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }
}
